// Anonymous-write transform. While meta.phase === 'write' and
// meta.revealed === false, other participants' text-bearing shapes render
// with a placeholder string instead of their actual content. Your own
// shapes always render as-is so you can edit what you wrote.
//
// Implementation note: this returns a NEW shape array with the text
// fields replaced — does not mutate the originals or the Y.Doc. The
// renderer downstream just sees the masked text.
//
// The mask is a centered-dot string sized to roughly match the original
// text length so the visual mass of stickies stays comparable (avoids
// telegraphing "this sticky is empty" vs "this sticky is a paragraph").

import type { RetroMeta, Shape } from '@frame/shared';

const MASK_CHAR = '•';
const MASK_MAX_LEN = 8;

// Mask a string to a dot run roughly proportional to its length but capped
// so a wall of text doesn't render as a wall of dots.
function maskText(s: string): string {
  if (!s) return s;
  const visible = s.trim().length;
  if (visible === 0) return s;
  // 1-2 chars → 1 dot, 3-5 → 2, 6-12 → 3, longer → 4-8.
  const dots = Math.min(MASK_MAX_LEN, Math.max(1, Math.ceil(Math.sqrt(visible))));
  return Array(dots).fill(MASK_CHAR).join(' ');
}

export function anonymizeShapes(
  shapes: Shape[],
  meta: RetroMeta,
  myAuthorId: string,
): Shape[] {
  // Only mask when we're explicitly in the anonymous half of the write phase.
  if (meta.mode !== 'retro' || meta.phase !== 'write' || meta.revealed) {
    return shapes;
  }
  return shapes.map((shape) => {
    if (shape.authorId === myAuthorId) return shape;
    if (!('text' in shape) || typeof shape.text !== 'string') return shape;
    return { ...shape, text: maskText(shape.text) } as Shape;
  });
}

// Exported for tests.
export const __maskText = maskText;
